/**
 * Vendor agnostic name based auto matcher for connection mappings.
 *
 * The function is **pure**: no IO, no time, no randomness. Given two lists of
 * companies plus the existing mapping context, it returns the set of new
 * mappings to create plus a structured breakdown of what it skipped and why.
 *
 * Used after the first successful Connect: the FE fetches BC + Aumico companies,
 * runs this matcher, posts the `toCreate` array to the bulk upsert endpoint.
 *
 * Algorithm (single deterministic rule, easy to support):
 *   matches when normalize(external.name) == normalize(aumico.name)
 *   normalize(s) := s.trim().toLowerCase().replace(/\s+/g, ' ')
 *
 * Ambiguity rule: if the normalized name produces collisions on EITHER side,
 * ALL colliding entries are skipped (never auto write a guess). The user
 * resolves these manually via single Edit on the row.
 *
 * Conflict rule: an Aumico Company already mapped to ANY connection of the
 * same vendor is excluded from match targets (the schema enforces one mapping
 * per `(aumicoCompanyId, vendor)` so the matcher must respect this).
 *
 * Idempotency rule: BC companies already mapped on THIS connection are
 * skipped (counted as `alreadyMapped`). Lets the matcher run safely against
 * partially mapped state, even though per spec it only runs once.
 *
 * No fuzzy matching, no legal form suffix stripping, no Levenshtein. The
 * cost of a wrong mapping (silent wrong data in reports) is much higher than
 * the cost of leaving a few unmapped for the user to resolve manually.
 */

export interface IAutoMatchInput {
    /** External (vendor side) companies under the connection. Normalized to `{id, name}` by the vendor adapter. */
    externalCompanies: ReadonlyArray<{ id: string; name: string }>;
    /** Aumico Companies in the organization. Normalized to `{id, name}`. */
    aumicoCompanies: ReadonlyArray<{ id: string; name: string }>;
    /** BC company IDs already mapped on THIS connection: used to skip on re run (defensive). */
    existingMappedExternalIds: ReadonlySet<string>;
    /** Aumico Company IDs already mapped to ANY connection of the same vendor: global uniqueness. */
    globallyMappedAumicoIds: ReadonlySet<string>;
}

export interface IAutoMatchToCreate {
    aumicoCompanyId: string;
    externalCompanyId: string;
    externalCompanyName: string;
}

export type AutoMatchSkipReason = 'EXTERNAL_DUPLICATE' | 'AUMICO_DUPLICATE';

export interface IAutoMatchOutput {
    /** Mappings to write via the bulk upsert endpoint. */
    toCreate: IAutoMatchToCreate[];
    skipped: {
        /** Count of BC companies whose ID was already in `existingMappedExternalIds`. */
        alreadyMapped: number;
        /** BC companies skipped because of name collisions on either side. */
        ambiguous: Array<{ externalCompanyName: string; reason: AutoMatchSkipReason }>;
        /** BC companies with no Aumico match at all. */
        noMatch: Array<{ externalCompanyName: string }>;
    };
}

/** The one normalization step used everywhere. Trim, lowercase, collapse whitespace. */
export function normalizeForMatch(s: string): string {
    return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function autoMatchByName(input: IAutoMatchInput): IAutoMatchOutput {
    const out: IAutoMatchOutput = {
        toCreate: [],
        skipped: {
            alreadyMapped: 0,
            ambiguous: [],
            noMatch: [],
        },
    };

    // ── Bucket both sides by normalized name. Skip already mapped on the way in. ──
    const externalByNorm = new Map<string, Array<{ id: string; name: string }>>();
    const aumicoByNorm = new Map<string, Array<{ id: string; name: string }>>();

    for (const ext of input.externalCompanies) {
        if (input.existingMappedExternalIds.has(ext.id)) {
            out.skipped.alreadyMapped++;
            continue;
        }
        const key = normalizeForMatch(ext.name);
        const bucket = externalByNorm.get(key);
        if (bucket) bucket.push(ext);
        else externalByNorm.set(key, [ext]);
    }

    for (const aum of input.aumicoCompanies) {
        if (input.globallyMappedAumicoIds.has(aum.id)) continue;
        const key = normalizeForMatch(aum.name);
        const bucket = aumicoByNorm.get(key);
        if (bucket) bucket.push(aum);
        else aumicoByNorm.set(key, [aum]);
    }

    // ── Walk external buckets, match against aumico buckets. ──
    for (const [key, externals] of externalByNorm) {
        const aumicos = aumicoByNorm.get(key) ?? [];

        if (aumicos.length === 0) {
            for (const ext of externals) {
                out.skipped.noMatch.push({ externalCompanyName: ext.name });
            }
            continue;
        }
        if (externals.length > 1) {
            for (const ext of externals) {
                out.skipped.ambiguous.push({
                    externalCompanyName: ext.name,
                    reason: 'EXTERNAL_DUPLICATE',
                });
            }
            continue;
        }
        if (aumicos.length > 1) {
            for (const ext of externals) {
                out.skipped.ambiguous.push({
                    externalCompanyName: ext.name,
                    reason: 'AUMICO_DUPLICATE',
                });
            }
            continue;
        }

        out.toCreate.push({
            aumicoCompanyId: aumicos[0].id,
            externalCompanyId: externals[0].id,
            externalCompanyName: externals[0].name,
        });
    }

    return out;
}
